<script>
  import Page from '@components/page'
  import Text from '@components/general/text.svelte'
  import Button from '@components/buttons/button.svelte'
  import CtaButton from '@components/buttons/cta-button.svelte'
  import FileIcon from '@components/general/file-icon.svelte'
  import ImageThumbnails from '@components/general/image-thumbnails.svelte'
  import Block from '@components/general/block'
  import GridItem from '@components/resources/grid-item.svelte'
  import PremiumItemBlock from '@components/general/premium-item-block.svelte'
  import FeedbackFab from '@components/feedbacks/fab.svelte'
  import { Video, Download, ExternalLink, Link, Pencil, ChevronRight } from 'lucide-svelte'
  import Tag from '@components/general/tag.svelte'
  import { router } from '@inertiajs/svelte'
  import { colors } from '@lib/utils/colors'
  import Image from '@components/general/image.svelte'
  import { cn } from '@lib/utils'
  import { formatDateText } from '@lib/utils/date'
  import { filesize } from 'filesize'

  let { resource, other_resources = [], feedback } = $props()

  let title = $derived(resource?.name || "Resource")

  let color = $derived(resource?.color || "gray")
  let colorObject = $derived(colors[color])

  let gradientClass = $derived(colorObject?.gradient || 'bg-gray-50/60')

  let contentClass = $derived(cn('prose', colorObject.selection, colorObject.marker))

  let isLink = $derived(resource.resource_type === 'link')
  let ctaText = $derived(!resource.can_access ? 'Upgrade to access' : isLink ? 'Visit Resource' : 'Download Now')
  let ctaIcon = $derived(isLink ? ExternalLink : Download)
  let ctaHref = $derived(isLink ? resource.url : resource.file?.url)

</script>

<Page.Layout {title}>

  <Page.Navigation {title} Icon={Video} 
    links={[{ href: '/resources', text: 'Resources' }]}
    adminActions={[
      { text: "Edit", icon: Pencil, path: `/admin/resources/${resource.id}/edit` }
    ]}
  />

  <Page.Content>

    <!-- A hero section with a color bg -->
    <section class={cn("relative rounded-sm shadow-md px-6 pt-18 min-h-[20vh] flex justify-between items-end gap-4 overflow-hidden", gradientClass)}>
      
      <!-- The title and CTA button -->
      <div class="relative z-10 flex flex-col gap-4 pb-6">

        <!-- The page title -->
        <Text variant="title" as="h1" text={resource?.name} class="sm:text-3xl" />

        <!-- The CTA button -->
        <CtaButton {color} text={ctaText} BeforeIcon={ctaIcon} externalPath={resource.can_access ? ctaHref : undefined} disabled={!resource.can_access} />
      
      </div>

      <!-- Cover image or decorative file/link icon -->
      {#if resource.cover_image}
        <Image src={resource.cover_image.thumb_url} alt={resource.cover_image.name} class="z-10 w-[clamp(6rem,15vw,10rem)] h-[clamp(6rem,14vw,9.5rem)] rounded-t-sm shadow-[0_2px_6px_4px_rgba(24,15,15,0.12)] object-cover flex-shrink-0" />
      {:else}
        <div class="absolute -bottom-4 -right-4 rotate-[-8deg] opacity-[0.07]">
          {#if isLink}
            <Link size={180} strokeWidth={0.75} />
          {:else}
            <FileIcon file={resource.file} size={180} strokeWidth={0.75} showHint={false} />
          {/if}
        </div>
      {/if}
      
    </section>

    {#if resource.can_access}
      <section class="page-content mt-4">

        <main>
          <div class={contentClass}>
            {@html resource?.description?.html}
          </div>
        </main>

        <aside>

          <Block.Items title="Resource details">
            <Block.Item label="Uploaded" value={formatDateText(resource.created_at)} />
            {#if resource.file}
              <Block.Item label="Type" value={resource.file.extension.toUpperCase()} />
              <Block.Item label="Size" value={filesize(resource.file.byte_size)} />
            {/if}
            {#if isLink}
              <Block.Item label="Type" value="External Link" />
            {/if}
            <Block.Item label="Topics">
              <div class="flex flex-wrap gap-1">
                {#each resource.tags ?? [] as tag (tag.id)}
                  <Tag {tag} size="xs" variant="faint" onSelect={() => router.visit(`/resources?tag=${tag.id}`)} />
                {:else}
                  <Tag tag={{ name: "Untagged", color: "gray" }} size="xs" variant="faint" />
                {/each}
              </div>
            </Block.Item>
          </Block.Items>

          <CtaButton size="sm" {color} text={isLink ? 'Visit Resource' : 'Download File'} BeforeIcon={ctaIcon} externalPath={ctaHref} class="ml-auto" />
        </aside>

      </section>
    {:else}
      <section class="mt-4">
        <PremiumItemBlock description="Upgrade your membership to access this resource" />
      </section>
    {/if}

    {#if other_resources.length > 0}

      <section class="page-content mt-4 full">

        <main class="full gap-2!">
          
          <div class="flex justify-between items-center mb-2">
            <Text variant="title" as="h4" text="Other resources" class="mb-0" />
            <Button variant="link" text="View All" Icon={ChevronRight} path="/resources" />
          </div>
          
          <!-- A grid of three other resources -->
          <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {#each other_resources as other_resource (other_resource.id)}
              <GridItem resource={other_resource} />
            {/each}
          </div>

        </main>

      </section>

    {/if}
    
  </Page.Content>
  
</Page.Layout>
<FeedbackFab
<FeedbackFab {feedback} record={{ id: resource.id, type: "Resource", name: resource.name }} />
